import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import {
  EditPenIcon,
  EmailIcon,
  LinkIcon,
  NoteIcon,
  PackageIcon,
  ShieldIcon,
} from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FormDeliveryChannelType } from 'constants/loanApplication';
import usePopover from 'hooks/usePopover';

import { openDialogAction, showMessageAction } from 'store/global/actions';
import {
  correctRecipientEmailAction,
  recordRecipientMailedDeliveryAction,
  reissueRecipientSigningLinkAction,
  switchRecipientAuthorizationAction,
} from 'store/loanApplications/actions';

import { RemediationActionType } from '../../DisclosureDelivery.constants';
import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';
import {
  getIsMailProofLinkAvailable,
  getIsRecipientRemediable,
  getIsRemediationUnavailableStatus,
} from '../../DisclosureDelivery.utils';
import useDisclosureDelivery from '../../hooks/useDisclosureDelivery';
import FixEmailDialog from '../FixEmailDialog';
import MarkAsMailedDialog from '../MarkAsMailedDialog';
import SwitchFactorDialog from '../SwitchFactorDialog';

const UNAVAILABLE_TOOLTIP = 'Remediation actions are not available yet';
const MAIL_RECEIPT_UNAVAILABLE_MESSAGE =
  "Proof of mailing isn't available yet — check back once the mail provider confirms the send";

const DisclosureSignerActions = ({
  signerItem,
  formDeliveryRequestId,
  isGeneratedOutside = false,
  onEditDates,
}) => {
  const dispatch = useDispatch();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const {
    isReadAllowed,
    isFormMutationAllowed,
    isEditGeneratedOutsideDatesAllowed,
    unavailableActions,
    onRemediationUnavailable,
    refresh,
  } = useDisclosureDelivery();

  const recipientRequestId = signerItem.formBundleSignatureRequestItemId;
  const isRemediable = getIsRecipientRemediable(signerItem);
  const isMailChannel = signerItem.channel === FormDeliveryChannelType.MAIL;
  // Once a recipient is on an automated mail dispatch (DEV-4795 records a DispatchProviderKey the
  // moment the letter is accepted), the manual "Mark as mailed" fallback no longer applies — its
  // status is now tracked automatically.
  const isOnAutomatedMailDispatch = isMailChannel && !!signerItem.dispatchProviderKey;
  const isMailProofLinkAvailable = getIsMailProofLinkAvailable(signerItem);
  const isMailProofLinkUrl = /^https?:\/\//i.test(signerItem.dispatchReference ?? '');
  const canEditGeneratedOutsideDates = isGeneratedOutside && isEditGeneratedOutsideDatesAllowed;

  const runRemediation = useCallback(
    async (actionType, thunk, params, successMessage) => {
      try {
        await dispatch(thunk({ ...params, silent: true }));

        dispatch(showMessageAction({ message: successMessage, variant: 'success' }));

        await refresh();

        return true;
      } catch (error) {
        const status = error?.response?.status;

        if (getIsRemediationUnavailableStatus(status)) {
          onRemediationUnavailable(actionType);
          dispatch(
            showMessageAction({
              message: UNAVAILABLE_TOOLTIP,
              variant: 'info',
            }),
          );

          return false;
        }

        dispatch(showMessageAction({ message: 'Could not complete the action', variant: 'error' }));

        return false;
      }
    },
    [dispatch, onRemediationUnavailable, refresh],
  );

  const onReissueLink = useCallback(() => {
    onClearAnchorElement();

    runRemediation(
      RemediationActionType.REISSUE_LINK,
      reissueRecipientSigningLinkAction,
      { formDeliveryRequestId, recipientRequestId },
      'A new signing link was issued and sent',
    );
  }, [onClearAnchorElement, runRemediation, formDeliveryRequestId, recipientRequestId]);

  const onSubmitEmail = useCallback(
    email =>
      runRemediation(
        RemediationActionType.FIX_EMAIL,
        correctRecipientEmailAction,
        { formDeliveryRequestId, recipientRequestId, email, resend: true },
        'The email was corrected and the notification resent',
      ),
    [runRemediation, formDeliveryRequestId, recipientRequestId],
  );

  const onSubmitFactor = useCallback(
    payload =>
      runRemediation(
        RemediationActionType.SWITCH_FACTOR,
        switchRecipientAuthorizationAction,
        { formDeliveryRequestId, recipientRequestId, ...payload },
        'The verification factor was updated',
      ),
    [runRemediation, formDeliveryRequestId, recipientRequestId],
  );

  const onSubmitMailed = useCallback(
    mailedDate =>
      runRemediation(
        RemediationActionType.MARK_AS_MAILED,
        recordRecipientMailedDeliveryAction,
        { formDeliveryRequestId, recipientRequestId, mailedDate },
        'Mailed delivery recorded',
      ),
    [runRemediation, formDeliveryRequestId, recipientRequestId],
  );

  const onFixEmail = useCallback(() => {
    onClearAnchorElement();

    dispatch(
      openDialogAction({
        header: 'Fix email & resend',
        body: () => (
          <FixEmailDialog currentEmail={signerItem.signerEmail} onSubmit={onSubmitEmail} />
        ),
      }),
    );
  }, [dispatch, onClearAnchorElement, onSubmitEmail, signerItem.signerEmail]);

  const onSwitchFactor = useCallback(() => {
    onClearAnchorElement();

    dispatch(
      openDialogAction({
        header: 'Switch verify factor',
        body: () => <SwitchFactorDialog onSubmit={onSubmitFactor} />,
      }),
    );
  }, [dispatch, onClearAnchorElement, onSubmitFactor]);

  const onMarkMailed = useCallback(() => {
    onClearAnchorElement();

    dispatch(
      openDialogAction({
        header: 'Mark as mailed',
        body: () => (
          <MarkAsMailedDialog signerName={signerItem.signerFullName} onSubmit={onSubmitMailed} />
        ),
      }),
    );
  }, [dispatch, onClearAnchorElement, onSubmitMailed, signerItem.signerFullName]);

  const onViewMailReceipt = useCallback(() => {
    onClearAnchorElement();

    if (isMailProofLinkUrl) {
      window.open(signerItem.dispatchReference, '_blank', 'noopener,noreferrer');

      return;
    }

    dispatch(showMessageAction({ message: MAIL_RECEIPT_UNAVAILABLE_MESSAGE, variant: 'info' }));
  }, [dispatch, onClearAnchorElement, isMailProofLinkUrl, signerItem.dispatchReference]);

  const onClickEditDates = useCallback(() => {
    onClearAnchorElement();
    onEditDates?.();
  }, [onClearAnchorElement, onEditDates]);

  const items = useMemo(() => {
    const buildItem = (actionType, text, onClick, icon) => {
      const isDisabled = !!unavailableActions?.[actionType];

      return {
        text,
        onClick,
        icon,
        isDisabled,
        disableTooltip: isDisabled ? UNAVAILABLE_TOOLTIP : undefined,
      };
    };

    return [
      // Reissue link / Fix email / Switch verify factor are e-signing concepts a physical-mail
      // recipient never has (no notification link, no email, no 2nd-factor challenge) — offering
      // them for a Mail-channel recipient would just error out. They also PUT/POST the Forms
      // recipient-request routes behind FormPolicies.Edit, so a holder without form.edit/form.manage
      // would only get a 403 from the click. A generated-outside package was signed entirely outside
      // Optimus, so those remediation actions never apply to it either.
      ...(isMailChannel || !isFormMutationAllowed || isGeneratedOutside
        ? []
        : [
            buildItem(
              RemediationActionType.REISSUE_LINK,
              'Reissue link',
              onReissueLink,
              <LinkIcon size={IconSize[20]} />,
            ),
            buildItem(
              RemediationActionType.FIX_EMAIL,
              'Fix email & resend',
              onFixEmail,
              <EmailIcon size={IconSize[20]} />,
            ),
            buildItem(
              RemediationActionType.SWITCH_FACTOR,
              'Switch verify factor',
              onSwitchFactor,
              <ShieldIcon size={IconSize[20]} />,
            ),
          ]),
      ...(isMailChannel
        ? [
            {
              text: 'View mail receipt',
              onClick: onViewMailReceipt,
              icon: <PackageIcon size={IconSize[20]} />,
              isDisabled: !isMailProofLinkAvailable,
              disableTooltip: isMailProofLinkAvailable
                ? undefined
                : MAIL_RECEIPT_UNAVAILABLE_MESSAGE,
              'data-testid': DisclosureDeliveryTestIds.MAIL_RECEIPT_ACTION,
            },
          ]
        : []),
      // The manual fallback stays hidden once an automated mail dispatch already tracks this
      // recipient's status — recording it again would just conflict with the tracked progress.
      // It also never applies to a generated-outside package (nothing was mailed from Optimus).
      ...(isOnAutomatedMailDispatch || !isFormMutationAllowed || isGeneratedOutside
        ? []
        : [
            buildItem(
              RemediationActionType.MARK_AS_MAILED,
              'Mark as mailed',
              onMarkMailed,
              <NoteIcon size={IconSize[20]} />,
            ),
          ]),
      ...(canEditGeneratedOutsideDates
        ? [
            {
              text: 'Edit dates',
              onClick: onClickEditDates,
              icon: <EditPenIcon size={IconSize[20]} />,
              'data-testid': DisclosureDeliveryTestIds.EDIT_DATES_ACTION,
            },
          ]
        : []),
    ];
  }, [
    unavailableActions,
    isFormMutationAllowed,
    isMailChannel,
    isOnAutomatedMailDispatch,
    isMailProofLinkAvailable,
    isGeneratedOutside,
    canEditGeneratedOutsideDates,
    onReissueLink,
    onFixEmail,
    onSwitchFactor,
    onMarkMailed,
    onViewMailReceipt,
    onClickEditDates,
  ]);

  // "View mail receipt" is a read affordance (it just opens the carrier's proof link), so a reader
  // without form.edit keeps it; for an e-sign recipient every item is a mutation, leaving nothing.
  // A signed/canceled generated-outside recipient is otherwise non-remediable, but Edit dates
  // corrects the package's recorded dates rather than the signing itself, so it must stay reachable.
  if (!isReadAllowed || (!isRemediable && !canEditGeneratedOutsideDates) || !items.length) {
    return null;
  }

  return (
    <>
      <MoreButtonCDS
        data-testid={DisclosureDeliveryTestIds.SIGNER_ACTION_BUTTON}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />
      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

DisclosureSignerActions.propTypes = {
  signerItem: PropTypes.shape({
    formBundleSignatureRequestItemId: PropTypes.string,
    signerEmail: PropTypes.string,
    signerFullName: PropTypes.string,
    status: PropTypes.string,
    channel: PropTypes.string,
    dispatchProviderKey: PropTypes.string,
    dispatchReference: PropTypes.string,
    mailClass: PropTypes.string,
  }).isRequired,
  formDeliveryRequestId: PropTypes.string,
  isGeneratedOutside: PropTypes.bool,
  onEditDates: PropTypes.func,
};

export default DisclosureSignerActions;
